"use client";

import { useEffect, useState } from "react";
import type { TabbedPanelItems } from "./TabbedPanel";
import { cn } from "@/lib/utils";
import { Typography } from "@/components/common/ui/Typography";

export interface TabbedPanelContentAreasProps<T extends string = string> {
  items: TabbedPanelItems;
  activeItem?: T;
  secondaryItem?: T | undefined;
  className?: string;
  onActiveItemChange?: (itemKey: T) => void;
  onSecondaryItemChange?: (itemKey?: T | undefined) => void;
}

interface ContentAreasState<T extends string = string> {
  isSettingSecondaryTab: boolean;
  isSettingPrimaryTab: boolean;
  isSecondTabHovered: boolean;
  draggedItem: T | null;
}

export function TabbedPanelContentAreas<T extends string = string>({
  items,
  activeItem,
  secondaryItem,
  className,
  onActiveItemChange,
  onSecondaryItemChange,
}: TabbedPanelContentAreasProps<T>) {
  const [state, setState] = useState<ContentAreasState<T>>({
    isSettingSecondaryTab: false,
    isSettingPrimaryTab: false,
    isSecondTabHovered: false,
    draggedItem: null,
  });

  const updateState = (updates: Partial<ContentAreasState<T>>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    tab: "secondary" | "primary",
  ) => {
    if (!e.dataTransfer.types.includes("application/x-tabbed-panel-item"))
      return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    if (tab === "primary") {
      updateState({ isSecondTabHovered: false, isSettingPrimaryTab: true });
    }

    if (tab === "secondary") {
      updateState({ isSecondTabHovered: true, isSettingPrimaryTab: false });
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const isLeavingContainer =
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom;

    if (isLeavingContainer) {
      updateState({ isSettingPrimaryTab: false, isSecondTabHovered: false });
    }
  };

  const handleDropPrimary = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const itemKey = e.dataTransfer.getData(
      "application/x-tabbed-panel-item",
    ) as T;
    if (itemKey && itemKey !== activeItem && itemKey !== secondaryItem) {
      onActiveItemChange?.(itemKey);
    }

    updateState({ isSettingPrimaryTab: false, isSecondTabHovered: false });
  };

  const handleDropSecondary = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const itemKey = e.dataTransfer.getData(
      "application/x-tabbed-panel-item",
    ) as T;
    if (itemKey && itemKey !== secondaryItem && itemKey !== activeItem) {
      onSecondaryItemChange?.(itemKey);
    }

    updateState({ isSettingPrimaryTab: false, isSecondTabHovered: false });
  };

  // Listen for events from TabbedPanel
  useEffect(() => {
    const handleDragStart = (e: Event) => {
      const customEvent = e as CustomEvent;
      updateState({
        isSettingSecondaryTab: true,
        draggedItem: customEvent.detail?.itemKey,
      });
    };

    const handleDragEnd = () => {
      updateState({
        draggedItem: null,
        isSettingSecondaryTab: false,
        isSettingPrimaryTab: false,
        isSecondTabHovered: false,
      });
    };

    const handleSecondaryMode = (e: Event) => {
      const customEvent = e as CustomEvent;
      const enabled = customEvent.detail?.enabled;
      updateState({
        isSettingSecondaryTab: enabled,
      });
    };

    window.addEventListener("tabbed-panel-drag-start", handleDragStart);
    window.addEventListener("tabbed-panel-drag-end", handleDragEnd);
    window.addEventListener(
      "tabbed-panel-secondary-mode",
      handleSecondaryMode,
    );

    return () => {
      window.removeEventListener("tabbed-panel-drag-start", handleDragStart);
      window.removeEventListener("tabbed-panel-drag-end", handleDragEnd);
      window.removeEventListener(
        "tabbed-panel-secondary-mode",
        handleSecondaryMode,
      );
    };
  }, []);

  return (
    <div
      id="tabbed-panel-content-wrapper"
      className={cn(
        "overflow-y-auto flex gap-3 items-start h-full",
        className,
      )}
    >
      <div
        id="tabbed-panel-content-primary"
        className={cn(
          "flex-1 transition-all duration-200 rounded-2xl h-full",
          state.isSettingPrimaryTab && "border-2 border-navigator-primary",
        )}
        onDragOver={(e) => handleDragOver(e, "primary")}
        onDragLeave={handleDragLeave}
        onDrop={handleDropPrimary}
      >
        {activeItem && items[activeItem] && items[activeItem].component}
      </div>

      {(secondaryItem || state.isSettingSecondaryTab) && (
        <>
          <div className="w-[1px] bg-gradient-to-b from-transparent via-gray-400 to-transparent h-full"></div>
          <div
            id="tabbed-panel-content-secondary"
            className={cn(
              "flex-1 h-full transition-all duration-200 rounded-2xl",
              state.isSettingSecondaryTab && "border-2 border-amber-500",
              !secondaryItem &&
                state.isSettingSecondaryTab &&
                !state.isSecondTabHovered &&
                "border-dashed bg-gray-100/50 flex items-center justify-center",
              state.isSecondTabHovered && "border-solid border-navigator-primary",
              state.isSecondTabHovered &&
                !secondaryItem &&
                "flex items-center justify-center",
            )}
            onDragOver={(e) => handleDragOver(e, "secondary")}
            onDragLeave={handleDragLeave}
            onDrop={handleDropSecondary}
          >
            {secondaryItem && items[secondaryItem] ? (
              items[secondaryItem].component
            ) : state.isSettingSecondaryTab ? (
              <div className="text-center">
                <Typography variant="headline4" weight="medium" color="light">Drop here</Typography>
                <Typography variant="body" color="light">to set secondary tab</Typography>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
